import { denormalize } from 'src/models/shared/normalizers';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { denormalizeTask, normalizeTask } from './normalizers';

import type { FetchUserTaskOptions, Task, UserTask } from './_types';

interface FetchTaskOptions {
  ids?: Task['id'][];
  page?: number;
  per?: number;
  fetchAll?: boolean;
}

// Function overloads for type safety
export async function fetchTasks(options: {
  ids: Task['id'][];
}): Promise<Task[]>;
export async function fetchTasks(
  options?: Omit<FetchTaskOptions, 'ids'>
): Promise<{ tasks: Task[]; total: number }>;
export async function fetchTasks(
  options?: FetchTaskOptions
): Promise<{ tasks: Task[]; total: number } | Task[]> {
  const { ids, page, per, fetchAll } = options || {};
  const params = {
    with_ids: ids?.join(','),
    page,
    per,
    fetch_all: fetchAll,
  };

  const response = await api.get(`/api/v4/clock/tasks`, {
    params: params,
  });

  const tasks = response.data?.task
    ? response.data.task.map((task: any) => normalizeTask(task))
    : response.data.map((task: any) => normalizeTask(task));

  if (ids?.length) {
    return tasks;
  }

  return {
    tasks,
    total: response.data?.meta?.total || tasks.length,
  };
}

export const fetchUserTasks = async (
  options?: FetchUserTaskOptions
): Promise<UserTask[]> => {
  const { fetchAll } = options || {};
  const params = {
    fetch_all: fetchAll,
  };

  const response = await api.get(`/api/v4/clock/user_tasks`, {
    params: params,
  });

  return response.data.map((task: any) => camelcaseKeys(task));
};

export const updateUserTask = async (
  userTaskId: UserTask['id'],
  userTask: Partial<UserTask>
): Promise<UserTask> => {
  const response = await api.put(
    `/api/v4/clock/user_tasks/${userTaskId}`,
    denormalize(userTask)
  );
  return camelcaseKeys(response.data);
};

export const createTask = async (task: Task): Promise<Task> => {
  const response = await api.post(`/api/v4/clock/tasks`, denormalizeTask(task));
  return normalizeTask(response.data);
};

export const updateTask = async (task: Task): Promise<Task> => {
  const response = await api.put(
    `/api/v4/clock/tasks/${task.id}`,
    denormalizeTask(task)
  );
  return normalizeTask(response.data);
};

export const deleteTask = async (task: Partial<Task>): Promise<Task> => {
  const response = await api.delete(`/api/v4/clock/tasks/${task.id}`);
  return normalizeTask(response.data);
};
